import { useState } from 'react';
import { buyFullVersion, FREE_TOWER_LEVEL, restoreFullVersion } from '../full-version';
import { type MessageKey, useT } from '../i18n';
import { sound } from '../sound';
import { useGame } from '../store';
import { Icon } from './icons';

/** What the full version opens, in the order the page lists it. */
const OPENS: MessageKey[] = [
  'full.opens.tower',
  'full.opens.aircraft',
  'full.opens.city',
  'full.opens.military',
  'full.opens.map',
  'full.opens.coop',
];

/**
 * The full version's page (the free Android game, see full-version.ts): what the player tried
 * that it opens, everything else it brings, its price, and buying or restoring it through Google
 * Play. Shown only when the player reaches for it; nothing nags in between.
 */
export function Paywall() {
  const t = useT();
  const reason = useGame((s) => s.paywall);
  const price = useGame((s) => s.fullPrice);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<MessageKey | null>(null);
  if (!reason) return null;
  const close = () => {
    setNote(null);
    useGame.setState({ paywall: null });
  };
  const run = async (what: () => Promise<boolean>, failed: MessageKey) => {
    setBusy(true);
    setNote(null);
    try {
      if (await what()) {
        sound.play('sign');
        setNote('full.thanks');
        window.setTimeout(close, 1600);
      } else {
        setNote(failed);
      }
    } catch {
      // Cancelled in Play's sheet, or Play not reachable.
      setNote('full.cancelled');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="modal-backdrop paywall-backdrop">
      <section className="modal panel paywall" role="dialog" aria-label={t('full.title')}>
        <header className="window-head">
          <span className="window-icon">
            <Icon name="crown" size={22} />
          </span>
          <div className="window-title">
            <h2>{t('full.title')}</h2>
            <span className="window-sub">{t(`full.reason.${reason}` as MessageKey)}</span>
          </div>
          <button type="button" className="icon-btn" title={t('hud.close')} onClick={close}>
            <Icon name="close" size={16} />
          </button>
        </header>
        <p className="paywall-lead">{t('full.lead', { level: FREE_TOWER_LEVEL })}</p>
        <ul className="paywall-list">
          {OPENS.map((key) => (
            <li key={key}>
              <Icon name="check" size={16} />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
        <p className="muted small">{t('full.once')}</p>
        <div className="actions paywall-actions">
          {/* With the buttons, so it is in sight however far the list is scrolled. */}
          {note && (
            <p className={`paywall-note small ${note === 'full.thanks' ? 'ok' : 'warn'}`}>
              {t(note)}
            </p>
          )}
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => run(restoreFullVersion, 'full.nothingToRestore')}
          >
            {t('full.restore')}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={close}>
            {t('full.later')}
          </button>
          <button
            type="button"
            className="btn primary big"
            disabled={busy}
            onClick={() => run(buyFullVersion, 'full.pending')}
          >
            <Icon name="crown" size={18} />
            {price ? t('full.buyPrice', { price }) : t('full.buy')}
          </button>
        </div>
      </section>
    </div>
  );
}
